import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, firstValueFrom, Subject } from 'rxjs';
import {
  AddCaseDocumentsDialogComponent
} from 'src/app/dialogs/add-case-documents-dialog/add-case-documents-dialog.component';
import { Case, CaseDocument, CaseType } from 'src/app/models/case';
import { Paginator } from 'src/app/models/paginator';
import { CaseService } from 'src/app/services/case.service';
import { DocumentService } from 'src/app/services/document.service';
import { MessageService } from 'src/app/services/message.service';
import { DocumentsCaseComponent } from '../../documents-display/documents-case/documents-case.component';
import { Document, DocumentFilters } from 'src/app/models/document';
import { PageEvent } from '@angular/material/paginator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProductService } from 'src/app/models/productService';

@Component({
  selector: 'app-case-proofs',
  templateUrl: './case-proofs.component.html',
  styleUrls: ['./case-proofs.component.scss']
})
export class CaseProofsComponent {
  CaseType = CaseType;

  @Input() case?: Case;
  @Input() readonly: boolean = false;

  @Input() productsServices?: ProductService[];
  @Input() trademarkProductsServices?: ProductService[];
  // countries of the case's property office, the only ones the territories filter offers
  @Input() relevantTerritories: string[] = [];

  public phase?: { name: string };

  public filters: DocumentFilters = {
    query: undefined,
    approval: [],
    classNumber: [],
    productsServices: [],
    territories: [],
    documentClass: [],
    trademark: [],
    trademarkFamily: [],
    period: {
      start: undefined,
      end: undefined
    },
    tags: []
  }

  public count: number = 0;
  public noDocs: boolean = false;

  public pager: Paginator<CaseDocument> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
    sort: '-createdAt'
  };
  filteredDocuments: CaseDocument[] = [];

  selection = new SelectionModel<CaseDocument>(true, []);

  @ViewChild('caseDocuments') caseDocuments?: DocumentsCaseComponent;

  @Output() refresh = new EventEmitter<any>();
  private readonly pagerSubject = new Subject<PageEvent>();

  constructor(
    private _message: MessageService,
    private caseService: CaseService,
    private dialog: MatDialog,
    private documentService: DocumentService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    this.pagerSubject
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntilDestroyed()
      ).subscribe(async () => {
        await this.retrieveDocuments(false);
      });
  }

  init() {
    this.resetPage();
    this.retrieveDocuments(true);
  }

  onFilterChange() {
    this.resetPage();
    this.retrieveDocuments(false);
  }

  async retrieveDocuments(init: boolean) {
    try {
      this._message.emitChange("LOADING", "START");
      if (this.case) {
        this.pager = {
          ...this.pager,
          ...await this.caseService.retrieveDocuments(
            this.case._id!,
            this.pager.page,
            this.pager.limit,
            this.pager.offset,
            this.pager.sort,
            this.filters.territories,
            this.filters.classNumber,
            this.filters.productsServices,
            this.filters.trademark,
            this.filters.trademarkFamily,
            this.filters.documentClass,
            this.filters.period,
            this.filters.nonUse,
            this.filters.approval,
            this.filters.query,
            this.filters.tags,
            this.filters.isConfidential
          )
        };
        this.filteredDocuments = this.pager.docs;
        // this.case.documents = this.filteredDocuments.map(doc => ({ ref: doc._id!, score: doc.score }));
        this.count = this.pager.totalDocs;
        if (init && this.pager.totalDocs === 0) {
          this.noDocs = true;
        } else if (init && this.pager.totalDocs > 0) {
          this.noDocs = false;
        }
      }
      this._message.emitChange("LOADING", "END");
    } catch (err) {
      console.error(err);
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  resetPage() {
    this.pager.limit = 10;
    this.pager.page = 1;
    this.pager.offset = 0;
  }

  async handlePage(event: any) {
    this.pager.limit = event.pageSize;
    this.pager.page = event.pageIndex + 1;
    this.pager.offset = event.pageIndex * this.pager.limit;
    this.pagerSubject.next(event);
  }

  onSortChange(event: any) {
    if (['asc', 'desc'].includes(event.direction)) {
      this.pager.sort = `${event.direction === 'desc' ? '-' : ''}${event.active}`;
      this.retrieveDocuments(false);
    }
  }

  async openUploadDialog() {
    this.documentService.openUploadDialog([], this.case);
  }

  async openLinkDialog() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      height: "calc(100% - 30px)",
      width: "calc(100% - 30px)",
      maxWidth: "100%",
      maxHeight: "100%",
      autoFocus: false,
      data: {
        case: this.case
      }
    }
    const dialog = this.dialog.open(AddCaseDocumentsDialogComponent, config);
    const result: { linked: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.linked && this.case?._id) {
      this.init();
      this.refresh.emit();
    }
  }

  async unlink() {
    try {
      if (this.case?._id) {
        this._message.emitChange("LOADING", "START");
        const documentsToUnlink = this.selection.selected.map((doc: Document) => doc._id!);
        await this.caseService.unlink(this.case._id, documentsToUnlink);
        this.init();
        this.refresh.emit();
        this._message.emitChange("LOADING", "END");
        this.toastr.success(this.translate.instant(documentsToUnlink.length > 1 ? 'CASE.UNLINK_PROOFS_SUCCESS' : 'CASE.UNLINK_PROOF_SUCCESS', { n: documentsToUnlink.length }));
        this.selection.clear();
        if (this.caseDocuments) {
          this.caseDocuments.flushSelection();
        }
      }
    } catch (err) {
      console.error(err);
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  async download() {
    try {
      if (this.case?._id) {
        this._message.emitChange("LOADING", "START");

        const selected = this.selection.selected.map(doc => doc._id!);
        await this.caseService.generateEvidenceRecord(this.case._id, [], selected)
        if (this.case.type === CaseType.RENEWAL_MAINTENANCE) {
          this.toastr.info('EVIDENCE_RECORDS.GENERATION_SUCCESS_ZIP_MESSAGE', 'EVIDENCE_RECORDS.GENERATION_SUCCESS_TITLE', { timeOut: 10000 });
        }
        else this.toastr.success('EVIDENCE_RECORDS.GENERATION_SUCCESS_MESSAGE', 'EVIDENCE_RECORDS.GENERATION_SUCCESS_TITLE');
        this._message.emitChange("LOADING", "END");
      }

    } catch (err: any) {
      console.error(err);
      this._message.emitChange("LOADING", "END");
      this.toastr.error(`ERRORS.GENERIC`);
    }
  }


}
